import { apiClient } from '../api/client';
import { getAccessToken, getIdToken, useAuthStore } from '../store/auth-store';
import { env } from '../config/env';
import { EventBus } from './event-bus';

export type NotificationsEvents = {
  open: Event;
  close: CloseEvent;
  message: MessageEvent;
  error: Event;
  unauthorized: void;
};

const RECONNECT_DELAYS = [1000, 2000, 5000, 10000, 15000];

type WebSocketResource = {
  url: string;
  protocols?: string[];
  queryParameters?: Record<string, string | undefined>;
};

export class NotificationsClient {
  private socket: WebSocket | null = null;

  private reconnectAttempts = 0;

  private readonly events = new EventBus<NotificationsEvents>();

  private shouldReconnect = true;

  private reconnectTimer: number | null = null;

  connect = async () => {
    this.shouldReconnect = true;
    await this.establishConnection();
  };

  disconnect = () => {
    this.shouldReconnect = false;
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.socket) {
      this.socket.close(1000, 'Client closed connection');
      this.socket = null;
    }
  };

  on = this.events.on.bind(this.events);

  private scheduleReconnect() {
    const delay = RECONNECT_DELAYS[Math.min(this.reconnectAttempts, RECONNECT_DELAYS.length - 1)];
    this.reconnectAttempts += 1;

    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
    }

    this.reconnectTimer = window.setTimeout(() => {
      this.establishConnection().catch((error) => {
        console.error('Failed to reconnect websocket', error);
        this.scheduleReconnect();
      });
    }, delay);
  }

  private async establishConnection() {
    try {
      const resource = await this.fetchConnectionResource();
      if (!resource) {
        return;
      }

      this.establishWebSocket(resource.url, resource.protocols);
    } catch (error) {
      console.error('Failed to establish notifications websocket connection', error);
      const errorEvent = new Event('error');
      this.events.emit('error', errorEvent);
      if (this.shouldReconnect) {
        this.scheduleReconnect();
      }
    }
  }

  private establishWebSocket(url: string, protocols?: string[]) {
    if (this.socket) {
      this.socket.close(1000, 'Client closed connection');
    }

    const socket = new WebSocket(url, protocols);
    this.socket = socket;

    socket.addEventListener('open', (event) => {
      this.reconnectAttempts = 0;
      this.events.emit('open', event);
    });

    socket.addEventListener('message', (event) => {
      this.events.emit('message', event);
    });

    socket.addEventListener('error', (event) => {
      this.events.emit('error', event);
    });

    socket.addEventListener('close', (event) => {
      this.events.emit('close', event);

      if (!this.shouldReconnect || event.code === 1000) {
        return;
      }

      this.scheduleReconnect();
    });
  }

  private async fetchConnectionResource(): Promise<WebSocketResource | null> {
    try {
      const token = getIdToken() ?? getAccessToken();

      if (!token) {
        console.debug('Notifications websocket skipped: no session tokens available');
        return null;
      }

      const result = await apiClient.POST('/notifications/connections', {
        body: {},
      });
      if (result.error) {
        if ((result.response?.status ?? 0) === 401) {
          this.events.emit('unauthorized', undefined);
          useAuthStore.getState().setSession(null);
          return null;
        }
        throw result.error;
      }

      const envelope = (result.data ?? {}) as {
        data?: {
          connectionUrl?: string;
          url?: string;
          websocket?: {
            url?: string;
            protocols?: string[];
          };
        };
        connectionUrl?: string;
        url?: string;
        websocket?: {
          url?: string;
          protocols?: string[];
        };
      };

      const payload = envelope?.data ?? envelope ?? {};
      const websocketResource = (payload.websocket ?? envelope.websocket ?? {}) as WebSocketResource;
      const connectionUrl =
        websocketResource.url ??
        payload.connectionUrl ??
        payload.url ??
        envelope.connectionUrl ??
        envelope.url;
      const queryParameters = websocketResource.queryParameters ?? {};

      if (connectionUrl) {
        try {
          const urlObject = new URL(connectionUrl);
          Object.entries(queryParameters).forEach(([key, value]) => {
            if (typeof value === 'string' && value.length > 0) {
              urlObject.searchParams.set(key, value);
            }
          });
          if (token) {
            urlObject.searchParams.set('token', token);
          }
          return {
            url: urlObject.toString(),
            protocols:
              websocketResource.protocols && websocketResource.protocols.length > 0
                ? websocketResource.protocols
                : undefined
          };
        } catch (_error) {
          console.warn('Failed to prepare notifications websocket URL');
        }
      }

      const url = new URL(env.wsUrl);
      if (token) {
        url.searchParams.set('token', token);
      }

      return {
        url: url.toString(),
        protocols:
          websocketResource.protocols && websocketResource.protocols.length > 0
            ? websocketResource.protocols
            : undefined
      };
    } catch (error) {
      console.error('Failed to initialize notifications websocket', error);
      return null;
    }
  }
}

export const notificationsClient = new NotificationsClient();
